import { Form as AntForm, notification } from 'antd';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Button } from 'src/components/Button/Button';
import { Card } from 'src/components/Card/Card';
import { Form } from 'src/components/Form/Form';
import { HiddenField } from 'src/components/Form/HiddenField';
import { TextAreaField } from 'src/components/Form/TextAreaField';
import { ValidationRules } from 'src/components/Form/ValidationRules';
import {
    LoanApplicationCommentForCreate,
    LoanApplicationCommentsStore,
} from 'src/stores/LoanApplicationCommentsStore/LoanApplicationCommentsStore';
import { CssColors } from 'src/styles/CssVariables';
import { ConstraintViolationError } from 'src/utils/ErrorDataHelper';
import { FormHelper } from 'src/utils/FormHelper';
import styled from 'styled-components';

type LoanApplicationCommentFormProps = {
    applicationId?: string;
};

export const LoanApplicationCommentForm = observer(
    ({ applicationId }: LoanApplicationCommentFormProps) => {
        const { t } = useTranslation();
        const [form] = AntForm.useForm<LoanApplicationCommentFormValues>();
        const comment: LoanApplicationCommentFormValues['comment'] =
            AntForm.useWatch(formNames.getPath('comment'), form);

        const fields = FormHelper.getFieldDataItems(
            store.createItemLoader.errorData as ConstraintViolationError,
        );

        const handleFinish = async (
            values: LoanApplicationCommentFormValues,
        ) => {
            const newItem = await store.create(values);

            if (!store.createItemLoader.hasError && newItem) {
                notification.success({
                    message: t('LoanApplication.Comments.Form.AddSuccess'),
                });
                form.resetFields([formNames.getPath('comment')]);
                store.pager.setPage(0);
                store.loadList();
            }
        };

        return (
            <Card title={t('LoanApplication.Comments.Form.Title')}>
                <Form form={form} onFinish={handleFinish} fields={fields}>
                    <HiddenField
                        name={formNames.getPath('id')}
                        value={applicationId}
                    />
                    <StyledTextAreaField
                        name={formNames.getPath('comment')}
                        rules={[ValidationRules.required]}
                        maxLength={4096}
                        textAreaProps={{
                            rows: 3,
                            placeholder: t(
                                'LoanApplication.Comments.Form.Placeholder',
                            ),
                        }}
                    />
                    <ButtonsContainer>
                        <StyledSubmitButton
                            type="primary"
                            htmlType="submit"
                            loading={store.createItemLoader.isLoading}
                            disabled={!comment?.trim()}
                        >
                            {t('LoanApplication.Comments.Form.Submit')}
                        </StyledSubmitButton>
                    </ButtonsContainer>
                </Form>
            </Card>
        );
    },
);

type LoanApplicationCommentFormValues = LoanApplicationCommentForCreate;

const formNames =
    FormHelper.createNameProvider<LoanApplicationCommentFormValues>();
const store = LoanApplicationCommentsStore;

const StyledTextAreaField = styled(TextAreaField)`
    .ant-input {
        padding: 8px 12px;
        border-radius: 6px;
    }
`;

const ButtonsContainer = styled.div`
    display: flex;
    align-items: center;
    justify-content: flex-end;
`;

const StyledSubmitButton = styled(Button)`
    &.ant-btn-primary[disabled],
    &.ant-btn-primary[disabled]:hover {
        background-color: ${CssColors.borderLightGrey};
        border-color: ${CssColors.borderLightGrey};
        color: ${CssColors.mainGray};
        opacity: 1;
    }
`;
